import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '@/App';
import { toast } from 'sonner';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import {
  Brain, Sparkles, Shield, Zap, CheckCircle, XCircle, Clock, AlertTriangle,
  Play, Pause, RotateCcw, Send, FileCode, Settings, BookOpen, BarChart3,
  ChevronRight, ChevronDown, RefreshCw, Search, Filter, ExternalLink,
  Users, Building, Server, Code, Terminal, GitBranch, CheckSquare,
  ThumbsUp, ThumbsDown, MessageSquare, TrendingUp, Target, Award,
  Cpu, FlaskConical, Workflow, ArrowRight, Loader2, Copy, Download,
  Trash2, HelpCircle, ArrowRightCircle
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from 'recharts';

export default function RemediationFactory() {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('issues');
  const [loading, setLoading] = useState(true);
  
  // Issues state
  const [issues, setIssues] = useState([]);
  const [issuesBySource, setIssuesBySource] = useState({});
  const [sourceFilter, setSourceFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [taskTeamFilter, setTaskTeamFilter] = useState('all');
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  
  // AI Fix state
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [aiFixLoading, setAiFixLoading] = useState(false);
  const [currentAiFix, setCurrentAiFix] = useState(null);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComments, setFeedbackComments] = useState('');
  
  // Task creation state
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    vulnerability_id: '',
    source: 'assessment_center',
    remediation_type: 'manual',
    assigned_team: 'security',
    assigned_to: '',
    priority: 'medium',
    notes: ''
  });
  
  // Users for assignment in create task dialog
  const [createTaskUsers, setCreateTaskUsers] = useState([]);
  
  // Approval chains state
  const [approvalChains, setApprovalChains] = useState([]);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedTaskForApproval, setSelectedTaskForApproval] = useState(null);
  const [approvalComments, setApprovalComments] = useState('');
  
  // Playbooks state
  const [playbooks, setPlaybooks] = useState([]);
  const [playbookDialogOpen, setPlaybookDialogOpen] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [playbookDetailOpen, setPlaybookDetailOpen] = useState(false);
  const [newPlaybook, setNewPlaybook] = useState({
    name: '',
    description: '',
    vulnerability_types: '',
    steps: '',
    estimated_time: ''
  });
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Ask AI state
  const [askAiQuestion, setAskAiQuestion] = useState('');
  const [askAiLoading, setAskAiLoading] = useState(false);
  const [askAiResponse, setAskAiResponse] = useState(null);
  const [askAiHistory, setAskAiHistory] = useState([]);
  
  // Delete task state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // User assignment state
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchIssues(),
      fetchTasks(),
      fetchApprovalChains(),
      fetchPlaybooks(),
      fetchAnalytics(),
      fetchAssignableUsers()
    ]);
    setLoading(false);
  };

  const fetchIssues = async () => {
    try {
      const res = await api.get('/remediation-factory/issues');
      setIssues(res.data.issues || []);
      setIssuesBySource(res.data.by_source || {});
    } catch (error) {
      console.error('Failed to fetch issues:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await api.get('/remediation-factory/tasks');
      setTasks(res.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchApprovalChains = async () => {
    try {
      const res = await api.get('/remediation-factory/approval-chains');
      setApprovalChains(res.data.chains || []);
    } catch (error) {
      console.error('Failed to fetch approval chains:', error);
    }
  };

  const fetchPlaybooks = async () => {
    try {
      const res = await api.get('/remediation-factory/playbooks');
      setPlaybooks(res.data.playbooks || []);
    } catch (error) {
      console.error('Failed to fetch playbooks:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/remediation-factory/analytics');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const generateAiFix = async (issue) => {
    setSelectedIssue(issue);
    setAiFixLoading(true);
    setFixDialogOpen(true);
    setCurrentAiFix(null);
    
    try {
      const res = await api.post('/remediation-factory/ai-fix', {
        vulnerability_id: issue.id,
        fix_type: 'auto',
        target_platform: issue.asset_type || 'generic',
        use_fallback: true  // Use rule-based generation for instant results
      });
      setCurrentAiFix(res.data.fix);
      if (res.data.fallback) {
        toast.success('Remediation plan generated (rule-based)');
      } else {
        toast.success('AI remediation plan generated');
      }
    } catch (error) {
      toast.error('Failed to generate AI fix');
      console.error(error);
    } finally {
      setAiFixLoading(false);
    }
  };

  const createRemediationTask = async () => {
    if (!selectedIssue) return;
    
    try {
      const taskData = {
        ...newTask,
        vulnerability_id: selectedIssue.id
      };
      // Remove empty assigned_to
      if (!taskData.assigned_to) {
        delete taskData.assigned_to;
      }
      
      await api.post('/remediation-factory/tasks', taskData);
      toast.success('Remediation task created');
      setCreateTaskDialogOpen(false);
      setNewTask({
        vulnerability_id: '',
        source: 'assessment_center',
        remediation_type: 'manual',
        assigned_team: 'security',
        assigned_to: '',
        priority: 'medium',
        notes: ''
      });
      setCreateTaskUsers([]);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  // Fetch users for create task dialog when team changes
  const fetchUsersForTeam = async (team) => {
    try {
      const res = await api.get(`/remediation-factory/assignable-users?team=${team}`);
      setCreateTaskUsers(res.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setCreateTaskUsers([]);
    }
  };

  // Handle team change in create task dialog
  const handleTeamChangeInCreateTask = (team) => {
    setNewTask({ ...newTask, assigned_team: team, assigned_to: '' });
    fetchUsersForTeam(team);
  };

  const approveTask = async (approved) => {
    if (!selectedTaskForApproval) return;
    
    try {
      await api.post(`/remediation-factory/tasks/${selectedTaskForApproval.id}/approve`, {
        approved,
        comments: approvalComments
      });
      toast.success(approved ? 'Task approved' : 'Task rejected');
      setApprovalDialogOpen(false);
      setApprovalComments('');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to process approval');
    }
  };

  const deployTask = async (taskId) => {
    try {
      await api.post(`/remediation-factory/tasks/${taskId}/deploy`);
      toast.success('Remediation deployed');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to deploy');
    }
  };

  const validateTask = async (taskId, passed) => {
    try {
      await api.post(`/remediation-factory/tasks/${taskId}/validate`, {
        passed,
        risk_reduction: passed ? 10 : 0,
        notes: passed ? 'Validation successful' : 'Validation failed'
      });
      toast.success(passed ? 'Validation passed' : 'Validation failed');
      fetchTasks();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to record validation');
    }
  };

  const submitFeedback = async () => {
    if (!currentAiFix) return;
    
    try {
      await api.post(`/remediation-factory/ai-fixes/${currentAiFix.id}/feedback`, {
        rating: feedbackRating,
        was_helpful: feedbackRating >= 3,
        was_accurate: feedbackRating >= 4,
        comments: feedbackComments
      });
      toast.success('Feedback submitted - thanks for helping us improve!');
      setFeedbackDialogOpen(false);
      setFeedbackRating(0);
      setFeedbackComments('');
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const createPlaybook = async () => {
    try {
      await api.post('/remediation-factory/playbooks', {
        ...newPlaybook,
        vulnerability_types: newPlaybook.vulnerability_types.split(',').map(s => s.trim()),
        steps: newPlaybook.steps.split('\n').filter(s => s.trim())
      });
      toast.success('Playbook created');
      setPlaybookDialogOpen(false);
      setNewPlaybook({ name: '', description: '', vulnerability_types: '', steps: '', estimated_time: '' });
      fetchPlaybooks();
    } catch (error) {
      toast.error('Failed to create playbook');
    }
  };

  // Ask AI function
  const askAiAboutFix = async () => {
    if (!askAiQuestion.trim()) return;
    
    setAskAiLoading(true);
    const question = askAiQuestion;
    setAskAiQuestion('');
    
    try {
      const res = await api.post('/remediation-factory/ask-ai', {
        question,
        context_type: 'general'
      });
      
      const newEntry = {
        id: Date.now(),
        question,
        response: res.data.response,
        fallback: res.data.fallback,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setAskAiResponse(newEntry);
      setAskAiHistory(prev => [newEntry, ...prev].slice(0, 10));
      
      if (res.data.fallback) {
        toast.info('Using cached knowledge base');
      }
    } catch (error) {
      toast.error('Failed to get AI response');
      console.error(error);
    } finally {
      setAskAiLoading(false);
    }
  };

  // Delete task function
  const deleteTask = async () => {
    if (!taskToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/remediation-factory/tasks/${taskToDelete.id}`);
      toast.success('Task deleted successfully');
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete task';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmDeleteTask = (task) => {
    setTaskToDelete(task);
    setDeleteConfirmOpen(true);
  };

  // Fetch assignable users
  const fetchAssignableUsers = async (team = null) => {
    try {
      const url = team ? `/remediation-factory/assignable-users?team=${team}` : '/remediation-factory/assignable-users';
      const res = await api.get(url);
      setAssignableUsers(res.data.users || []);
    } catch (error) {
      console.error('Failed to fetch assignable users:', error);
    }
  };

  // Task progression functions
  const startTask = async (taskId) => {
    try {
      await api.post(`/remediation-factory/tasks/${taskId}/start`);
      toast.success('Task started - In Progress');
      fetchTasks();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start task';
      toast.error(message);
    }
  };

  const submitForApproval = async (taskId) => {
    try {
      await api.post(`/remediation-factory/tasks/${taskId}/submit-for-approval`);
      toast.success('Task submitted for approval');
      fetchTasks();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit task';
      toast.error(message);
    }
  };

  const openAssignDialog = (task) => {
    setTaskToAssign(task);
    setSelectedUserId(task.assigned_to || '');
    fetchAssignableUsers(task.assigned_team);
    setAssignDialogOpen(true);
  };

  const assignUserToTask = async () => {
    if (!taskToAssign || !selectedUserId) return;
    
    try {
      await api.post(`/remediation-factory/tasks/${taskToAssign.id}/assign`, {
        assigned_to: selectedUserId
      });
      toast.success('User assigned to task');
      setAssignDialogOpen(false);
      setTaskToAssign(null);
      setSelectedUserId('');
      fetchTasks();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to assign user';
      toast.error(message);
    }
  };

  // Filtered data
  const filteredIssues = useMemo(() => {
    return issues.filter(issue => {
      const matchesSearch = !searchTerm || 
        issue.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.cve_id?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPriority = priorityFilter === 'all' || issue.severity === priorityFilter;
      const matchesSource = sourceFilter === 'all' || 
        (sourceFilter === 'threat_intel' && ['raptor', 'threat_feed', 'intel'].includes(issue.source)) ||
        (sourceFilter === 'asset_management' && ['easm', 'discovery', 'asset_scan'].includes(issue.source)) ||
        (sourceFilter === 'assessment_center' && !['raptor', 'threat_feed', 'intel', 'easm', 'discovery', 'asset_scan'].includes(issue.source));
      return matchesSearch && matchesPriority && matchesSource;
    });
  }, [issues, searchTerm, priorityFilter, sourceFilter]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const matchesStatus = taskStatusFilter === 'all' || task.status === taskStatusFilter;
      const matchesTeam = taskTeamFilter === 'all' || task.assigned_team === taskTeamFilter;
      const matchesSearch = taskSearchTerm === '' || 
        task.vulnerability?.title?.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        task.remediation_type?.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        task.assigned_team?.toLowerCase().includes(taskSearchTerm.toLowerCase());
      return matchesStatus && matchesTeam && matchesSearch;
    });
  }, [tasks, taskStatusFilter, taskTeamFilter, taskSearchTerm]);

  // Select all tasks toggle
  const toggleSelectAllTasks = () => {
    if (selectedTasks.size === filteredTasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(filteredTasks.map(t => t.id)));
    }
  };

  // Toggle individual task selection
  const toggleTaskSelection = (taskId) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  // Bulk operations for tasks
  const handleBulkTaskAction = async (action) => {
    if (selectedTasks.size === 0) {
      toast.warning('No tasks selected');
      return;
    }

    const taskIds = Array.from(selectedTasks);
    let successCount = 0;
    let failCount = 0;

    try {
      if (action === 'delete') {
        if (!window.confirm(`Delete ${selectedTasks.size} task(s)?`)) return;
        for (const id of taskIds) {
          try {
            await api.delete(`/remediation-factory/tasks/${id}`);
            successCount++;
          } catch (e) {
            failCount++;
          }
        }
        toast.success(`Deleted ${successCount} task(s)`);
      } else if (action === 'start') {
        for (const id of taskIds) {
          try {
            await api.post(`/remediation-factory/tasks/${id}/start`);
            successCount++;
          } catch (e) {
            failCount++;
          }
        }
        toast.success(`Started ${successCount} task(s)`);
      } else if (action === 'complete') {
        for (const id of taskIds) {
          try {
            await api.post(`/remediation-factory/tasks/${id}/submit-for-approval`);
            successCount++;
          } catch (e) {
            failCount++;
          }
        }
        toast.success(`Moved ${successCount} task(s) to awaiting approval`);
      }

      if (failCount > 0) {
        toast.error(`Failed to process ${failCount} task(s)`);
      }

      setSelectedTasks(new Set());
      fetchTasks();
    } catch (error) {
      toast.error('Bulk operation failed');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return colors[severity] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-zinc-500/20 text-zinc-400',
      in_progress: 'bg-blue-500/20 text-blue-400',
      awaiting_approval: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-emerald-500/20 text-emerald-400',
      rejected: 'bg-red-500/20 text-red-400',
      deployed: 'bg-indigo-500/20 text-indigo-400',
      verified: 'bg-green-500/20 text-green-400',
      failed: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || colors.pending;
  };

  const getTeamIcon = (team) => {
    const icons = {
      dev: <Code className="w-4 h-4" />,
      secops: <Shield className="w-4 h-4" />,
      it: <Server className="w-4 h-4" />,
      security: <Shield className="w-4 h-4" />
    };
    return icons[team] || <Users className="w-4 h-4" />;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Component sections
  const ModuleCard = ({ icon: Icon, title, description, color, onClick }) => (
    <div
      onClick={onClick}
      className={`p-5 rounded-xl border cursor-pointer transition-all hover:scale-[1.02] ${
        isDarkMode 
          ? 'bg-zinc-900/40 border-white/10 hover:border-indigo-500/30' 
          : 'bg-white border-slate-200 hover:border-indigo-300 shadow-sm'
      }`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
      <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{description}</p>
    </div>
  );

  const IssueCard = ({ issue }) => (
    <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-xs uppercase border ${getSeverityColor(issue.severity)}`}>
              {issue.severity}
            </span>
            {issue.cve_id && (
              <span className="text-xs font-mono text-cyan-500">{issue.cve_id}</span>
            )}
          </div>
          <h4 className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{issue.title}</h4>
          <p className={`text-sm line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{issue.description}</p>
          <div className={`flex items-center gap-3 mt-2 text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
            <span>{issue.asset_name || 'Unknown Asset'}</span>
            <span>•</span>
            <span>{new Date(issue.discovered_at).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            onClick={() => generateAiFix(issue)}
            className="bg-violet-600 hover:bg-violet-500 text-xs"
          >
            <Brain className="w-3 h-3 mr-1" />
            AI Fix
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setSelectedIssue(issue); setCreateTaskDialogOpen(true); }}
            className={isDarkMode ? 'border-white/10' : ''}
          >
            <Zap className="w-3 h-3 mr-1" />
            Task
          </Button>
        </div>
      </div>
    </div>
  );

  const TaskCard = ({ task }) => {
    const workflowStages = [
      { key: 'pending', label: 'Created', icon: Clock },
      { key: 'in_progress', label: 'In Progress', icon: Workflow },
      { key: 'awaiting_approval', label: 'Approval', icon: CheckSquare },
      { key: 'approved', label: 'Approved', icon: CheckCircle },
      { key: 'deployed', label: 'Deployed', icon: Play },
      { key: 'verified', label: 'Verified', icon: Shield }
    ];
    
    const currentStageIdx = workflowStages.findIndex(s => s.key === task.status);
    const isCompleted = task.status === 'verified';
    const isFailed = task.status === 'failed';
    
    return (
      <div data-testid={`task-card-${task.id}`} className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
        {/* Workflow Pipeline Visualization */}
        <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-xs font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Workflow Progress</span>
            <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(task.status)}`}>
              {task.status?.replace('_', ' ')}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {workflowStages.map((stage, idx) => {
              const StageIcon = stage.icon;
              const isActive = idx === currentStageIdx;
              const isPassed = idx < currentStageIdx || isCompleted;
              
              return (
                <div key={stage.key} className="flex items-center flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                    isPassed ? 'bg-green-500/20 text-green-400' :
                    isActive ? 'bg-indigo-500/20 text-indigo-400 ring-2 ring-indigo-500/50' :
                    isFailed && idx === currentStageIdx ? 'bg-red-500/20 text-red-400' :
                    isDarkMode ? 'bg-zinc-700/50 text-zinc-500' : 'bg-slate-200 text-slate-400'
                  }`}>
                    <StageIcon className="w-3.5 h-3.5" />
                  </div>
                  {idx < workflowStages.length - 1 && (
                    <div className={`flex-1 h-1 mx-1 rounded ${
                      isPassed ? 'bg-green-500/50' :
                      isDarkMode ? 'bg-zinc-700' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            {workflowStages.map((stage, idx) => (
              <span key={stage.key} className={`text-[9px] ${
                idx <= currentStageIdx ? (isDarkMode ? 'text-zinc-400' : 'text-slate-600') : (isDarkMode ? 'text-zinc-600' : 'text-slate-400')
              }`}>{stage.label}</span>
            ))}
          </div>
        </div>
        
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded text-xs uppercase border ${getSeverityColor(task.priority)}`}>
                {task.priority}
              </span>
              {task.auto_created && (
                <span className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-600'}`}>
                  Auto-created
                </span>
              )}
            </div>
            <h4 className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {task.vulnerability?.title || 'Remediation Task'}
            </h4>
            
            {/* Team & Assigned User Display */}
            <div className={`flex items-center gap-3 text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
              <span className="flex items-center gap-1">
                {getTeamIcon(task.assigned_team)}
                <span className="font-medium">{task.assigned_team}</span>
              </span>
              <span>•</span>
              <span>{task.remediation_type?.replace('_', ' ')}</span>
            </div>
            
            {/* Assigned User with Avatar */}
            <div className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-zinc-800/30' : 'bg-slate-100'}`}>
              {task.assigned_to_user ? (
                <>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium text-white ${task.assigned_to_user.avatar_color || 'bg-violet-500'}`}>
                    {task.assigned_to_user.initials || task.assigned_to_user.name?.substring(0, 2).toUpperCase() || '??'}
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs font-medium ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
                      {task.assigned_to_user.name || task.assigned_to_user.email}
                    </p>
                    <p className={`text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {task.assigned_to_user.role}
                    </p>
                  </div>
                </>
              ) : (
                <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  No user assigned
                </span>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openAssignDialog(task)}
                className={`h-6 px-2 text-xs ${isDarkMode ? 'text-indigo-400 hover:bg-indigo-500/10' : 'text-indigo-600 hover:bg-indigo-50'}`}
                data-testid={`assign-user-${task.id}`}
              >
                <Users className="w-3 h-3 mr-1" />
                {task.assigned_to_user ? 'Change' : 'Assign'}
              </Button>
            </div>
            
            {/* Approval Chain */}
            {task.approval_chain?.length > 0 && (
              <div className="mt-2">
                <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Approval Chain:</span>
                <div className="flex items-center gap-1 mt-1">
                  {task.approval_chain.map((step, idx) => (
                    <div key={idx} className="flex items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        step.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        step.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        idx === task.current_approval_step ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {step.status === 'approved' ? <CheckCircle className="w-3 h-3" /> :
                         step.status === 'rejected' ? <XCircle className="w-3 h-3" /> :
                         idx + 1}
                      </div>
                      {idx < task.approval_chain.length - 1 && (
                        <div className={`w-4 h-0.5 ${step.status === 'approved' ? 'bg-green-500/50' : 'bg-zinc-600'}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col gap-1">
            {/* Pending -> Start Work */}
            {task.status === 'pending' && (
              <Button
                size="sm"
                onClick={() => startTask(task.id)}
                className="bg-blue-600 hover:bg-blue-500 text-xs"
                data-testid={`start-task-${task.id}`}
              >
                <Play className="w-3 h-3 mr-1" />
                Start Work
              </Button>
            )}
            
            {/* In Progress -> Submit for Approval */}
            {task.status === 'in_progress' && (
              <Button
                size="sm"
                onClick={() => submitForApproval(task.id)}
                className="bg-amber-600 hover:bg-amber-500 text-xs"
                data-testid={`submit-approval-${task.id}`}
              >
                <ArrowRight className="w-3 h-3 mr-1" />
                Submit
              </Button>
            )}
            
            {/* Awaiting Approval -> Review */}
            {task.status === 'awaiting_approval' && (
              <Button
                size="sm"
                onClick={() => { setSelectedTaskForApproval(task); setApprovalDialogOpen(true); }}
                className="bg-yellow-600 hover:bg-yellow-500 text-xs"
              >
                <CheckSquare className="w-3 h-3 mr-1" />
                Review
              </Button>
            )}
            
            {/* Approved -> Deploy */}
            {task.status === 'approved' && (
              <Button
                size="sm"
                onClick={() => deployTask(task.id)}
                className="bg-indigo-600 hover:bg-indigo-500 text-xs"
              >
                <Play className="w-3 h-3 mr-1" />
                Deploy
              </Button>
            )}
            
            {/* Deployed -> Validate */}
            {task.status === 'deployed' && (
              <>
                <Button
                  size="sm"
                  onClick={() => validateTask(task.id, true)}
                  className="bg-green-600 hover:bg-green-500 text-xs"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Pass
                </Button>
                <Button
                  size="sm"
                  onClick={() => validateTask(task.id, false)}
                  className="bg-red-600 hover:bg-red-500 text-xs"
                >
                  <XCircle className="w-3 h-3 mr-1" />
                  Fail
                </Button>
              </>
            )}
            
            {/* Delete button - only for non-completed tasks */}
            {!isCompleted && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => confirmDeleteTask(task)}
                className={`text-xs ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                data-testid={`delete-task-${task.id}`}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen p-4 lg:p-6 ${isDarkMode ? 'bg-zinc-950' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`font-bold text-2xl lg:text-3xl flex items-center gap-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <Workflow className="w-5 h-5 text-white" />
            </div>
            Remediation Factory
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
            AI-powered vulnerability remediation and orchestration
          </p>
        </div>
        <Button onClick={fetchAllData} variant="outline" className={isDarkMode ? 'border-white/10' : ''}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Module Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <ModuleCard
          icon={Brain}
          title="AI Agent"
          description="Autonomous analysis"
          color="bg-violet-500/20 text-violet-400"
          onClick={() => setActiveTab('issues')}
        />
        <ModuleCard
          icon={FileCode}
          title="Fix Studio"
          description="Code patches"
          color="bg-blue-500/20 text-blue-400"
          onClick={() => setActiveTab('issues')}
        />
        <ModuleCard
          icon={Workflow}
          title="Orchestrator"
          description="Task routing"
          color="bg-indigo-500/20 text-indigo-400"
          onClick={() => setActiveTab('tasks')}
        />
        <ModuleCard
          icon={FlaskConical}
          title="Sandbox"
          description="Safe testing"
          color="bg-emerald-500/20 text-emerald-400"
          onClick={() => setActiveTab('tasks')}
        />
        <ModuleCard
          icon={BookOpen}
          title="Playbooks"
          description="Templates"
          color="bg-orange-500/20 text-orange-400"
          onClick={() => setActiveTab('playbooks')}
        />
        <ModuleCard
          icon={BarChart3}
          title="Analytics"
          description="Metrics"
          color="bg-cyan-500/20 text-cyan-400"
          onClick={() => setActiveTab('analytics')}
        />
        <ModuleCard
          icon={HelpCircle}
          title="Ask AI"
          description="Get answers"
          color="bg-pink-500/20 text-pink-400"
          onClick={() => setActiveTab('askai')}
        />
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`mb-4 ${isDarkMode ? 'bg-zinc-900/50' : 'bg-slate-100'}`}>
          <TabsTrigger value="issues" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            Issues ({issues.length})
          </TabsTrigger>
          <TabsTrigger value="tasks" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            Tasks ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="playbooks" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            Playbooks
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            Analytics
          </TabsTrigger>
          <TabsTrigger value="askai" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
            <HelpCircle className="w-3 h-3 mr-1" />
            Ask AI
          </TabsTrigger>
        </TabsList>

        {/* Issues Tab */}
        <TabsContent value="issues" className="space-y-4">
          {/* Source Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div 
              onClick={() => setSourceFilter('threat_intel')}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                sourceFilter === 'threat_intel' ? 'border-red-500/50' : ''
              } ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {issuesBySource.threat_intel || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Threat Intelligence</p>
                </div>
              </div>
            </div>
            <div 
              onClick={() => setSourceFilter('asset_management')}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                sourceFilter === 'asset_management' ? 'border-blue-500/50' : ''
              } ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Server className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {issuesBySource.asset_management || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Asset Management</p>
                </div>
              </div>
            </div>
            <div 
              onClick={() => setSourceFilter('assessment_center')}
              className={`p-4 rounded-xl border cursor-pointer transition-all ${
                sourceFilter === 'assessment_center' ? 'border-emerald-500/50' : ''
              } ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {issuesBySource.assessment_center || 0}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Assessment Center</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                <Input
                  placeholder="Search vulnerabilities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                />
              </div>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}>
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              {sourceFilter !== 'all' && (
                <Button variant="outline" size="sm" onClick={() => setSourceFilter('all')} className={isDarkMode ? 'border-white/10' : ''}>
                  Clear Filter
                </Button>
              )}
            </div>
          </div>

          {/* Issues List */}
          <div className="space-y-3">
            {filteredIssues.length === 0 ? (
              <div className={`p-12 text-center rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                <Shield className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>No vulnerabilities found</p>
              </div>
            ) : (
              filteredIssues.slice(0, 20).map(issue => (
                <IssueCard key={issue.id} issue={issue} />
              ))
            )}
            {filteredIssues.length > 20 && (
              <p className={`text-center text-sm ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Showing 20 of {filteredIssues.length} issues
              </p>
            )}
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          {/* Task Filters */}
          <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                <Input
                  placeholder="Search tasks..."
                  value={taskSearchTerm}
                  onChange={(e) => setTaskSearchTerm(e.target.value)}
                  className={`pl-9 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                  data-testid="task-search"
                />
              </div>
              <Select value={taskStatusFilter} onValueChange={setTaskStatusFilter}>
                <SelectTrigger className={`w-48 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="awaiting_approval">Awaiting Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="deployed">Deployed</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={taskTeamFilter} onValueChange={setTaskTeamFilter}>
                <SelectTrigger className={`w-40 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}>
                  <Users className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Team" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="all">All Teams</SelectItem>
                  <SelectItem value="dev">Development</SelectItem>
                  <SelectItem value="secops">SecOps</SelectItem>
                  <SelectItem value="it">IT</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAllTasks}
                className={isDarkMode ? 'border-white/10' : ''}
                data-testid="select-all-tasks"
              >
                <CheckSquare className="w-4 h-4 mr-1" />
                {selectedTasks.size === filteredTasks.length && filteredTasks.length > 0 ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            {selectedTasks.size > 0 && (
              <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'} flex items-center gap-3`}>
                <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  <strong>{selectedTasks.size}</strong> task(s) selected
                </span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleBulkTaskAction('start')} className="bg-blue-600 hover:bg-blue-500 text-white text-xs">
                    <Play className="w-3 h-3 mr-1" />
                    Start
                  </Button>
                  <Button size="sm" onClick={() => handleBulkTaskAction('complete')} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Mark Complete
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleBulkTaskAction('delete')} className={`text-xs ${isDarkMode ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}>
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Tasks List */}
          <div className="space-y-3">
            {filteredTasks.length === 0 ? (
              <div className={`p-12 text-center rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                <Zap className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>No remediation tasks</p>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>
                  Create tasks from the Issues tab
                </p>
              </div>
            ) : (
              filteredTasks.map(task => (
                <TaskCard key={task.id} task={task} />
              ))
            )}
          </div>
        </TabsContent>

        {/* Playbooks Tab */}
        <TabsContent value="playbooks" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Remediation Playbook Library
            </h2>
            <Dialog open={playbookDialogOpen} onOpenChange={setPlaybookDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-500">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Create Playbook
                </Button>
              </DialogTrigger>
              <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                <DialogHeader>
                  <DialogTitle className={isDarkMode ? 'text-white' : ''}>Create Playbook</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className={isDarkMode ? 'text-zinc-300' : ''}>Name</Label>
                    <Input
                      value={newPlaybook.name}
                      onChange={(e) => setNewPlaybook({...newPlaybook, name: e.target.value})}
                      className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className={isDarkMode ? 'text-zinc-300' : ''}>Description</Label>
                    <Textarea
                      value={newPlaybook.description}
                      onChange={(e) => setNewPlaybook({...newPlaybook, description: e.target.value})}
                      className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className={isDarkMode ? 'text-zinc-300' : ''}>Vulnerability Types (comma-separated)</Label>
                    <Input
                      value={newPlaybook.vulnerability_types}
                      onChange={(e) => setNewPlaybook({...newPlaybook, vulnerability_types: e.target.value})}
                      placeholder="sql_injection, xss, rce"
                      className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className={isDarkMode ? 'text-zinc-300' : ''}>Steps (one per line)</Label>
                    <Textarea
                      value={newPlaybook.steps}
                      onChange={(e) => setNewPlaybook({...newPlaybook, steps: e.target.value})}
                      rows={5}
                      className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className={isDarkMode ? 'text-zinc-300' : ''}>Estimated Time</Label>
                    <Input
                      value={newPlaybook.estimated_time}
                      onChange={(e) => setNewPlaybook({...newPlaybook, estimated_time: e.target.value})}
                      placeholder="2-4 hours"
                      className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createPlaybook} className="bg-indigo-600 hover:bg-indigo-500">
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {playbooks.map(playbook => (
              <div 
                key={playbook.id} 
                onClick={() => { setSelectedPlaybook(playbook); setPlaybookDetailOpen(true); }}
                className={`p-5 rounded-xl border cursor-pointer transition-all hover:scale-[1.02] ${isDarkMode ? 'bg-zinc-900/40 border-white/10 hover:border-orange-500/50 hover:shadow-lg hover:shadow-orange-500/10' : 'bg-white border-slate-200 hover:border-orange-400 hover:shadow-md'}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{playbook.name}</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      {playbook.estimated_time || 'Time varies'}
                    </p>
                  </div>
                  <ChevronRight className={`w-5 h-5 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
                </div>
                <p className={`text-sm mb-3 line-clamp-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{playbook.description}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {playbook.vulnerability_types?.slice(0, 3).map((type, idx) => (
                    <span key={idx} className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-600'}`}>
                      {type.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {playbook.vulnerability_types?.length > 3 && (
                    <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-600'}`}>
                      +{playbook.vulnerability_types.length - 3} more
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {playbook.steps?.length || 0} steps
                  </div>
                  {playbook.severity_levels && (
                    <div className="flex gap-1">
                      {playbook.severity_levels.includes('critical') && <span className="w-2 h-2 rounded-full bg-red-500" title="Critical" />}
                      {playbook.severity_levels.includes('high') && <span className="w-2 h-2 rounded-full bg-orange-500" title="High" />}
                      {playbook.severity_levels.includes('medium') && <span className="w-2 h-2 rounded-full bg-yellow-500" title="Medium" />}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Playbook Detail Dialog */}
          <Dialog open={playbookDetailOpen} onOpenChange={setPlaybookDetailOpen}>
            <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10' : ''}`}>
              {selectedPlaybook && (
                <>
                  <DialogHeader>
                    <DialogTitle className={`flex items-center gap-3 ${isDarkMode ? 'text-white' : ''}`}>
                      <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-orange-400" />
                      </div>
                      {selectedPlaybook.name}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-6 mt-4">
                    {/* Overview */}
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                      <p className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>{selectedPlaybook.description}</p>
                      <div className="flex flex-wrap gap-4 mt-4">
                        <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          <Clock className="w-4 h-4 inline mr-1" />
                          {selectedPlaybook.estimated_time || 'Time varies'}
                        </div>
                        {selectedPlaybook.risk_if_unpatched && (
                          <div className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                            <AlertTriangle className="w-4 h-4 inline mr-1" />
                            {selectedPlaybook.risk_if_unpatched}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Vulnerability Types */}
                    <div>
                      <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Applicable Vulnerability Types
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedPlaybook.vulnerability_types?.map((type, idx) => (
                          <span key={idx} className={`text-sm px-3 py-1 rounded-full ${isDarkMode ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                            {type.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Affected Technologies */}
                    {selectedPlaybook.affected_technologies && (
                      <div>
                        <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Affected Technologies
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedPlaybook.affected_technologies.map((tech, idx) => (
                            <span key={idx} className={`text-sm px-3 py-1 rounded-full ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Severity Levels */}
                    {selectedPlaybook.severity_levels && (
                      <div>
                        <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Severity Levels Addressed
                        </h4>
                        <div className="flex gap-2">
                          {selectedPlaybook.severity_levels.map((level, idx) => (
                            <span key={idx} className={`text-sm px-3 py-1 rounded-full ${
                              level === 'critical' ? 'bg-red-500/20 text-red-400' :
                              level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                              level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {level.charAt(0).toUpperCase() + level.slice(1)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Remediation Steps */}
                    <div>
                      <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        Remediation Steps
                      </h4>
                      <div className="space-y-3">
                        {selectedPlaybook.steps?.map((step, idx) => (
                          <div key={idx} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-800/30 border-white/5' : 'bg-white border-slate-200'}`}>
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isDarkMode ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                                {typeof step === 'object' ? step.step : idx + 1}
                              </div>
                              <div className="flex-1">
                                <h5 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                  {typeof step === 'object' ? step.title : step}
                                </h5>
                                {typeof step === 'object' && step.description && (
                                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                                    {step.description}
                                  </p>
                                )}
                                {typeof step === 'object' && step.time_estimate && (
                                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    {step.time_estimate}
                                  </p>
                                )}
                                {typeof step === 'object' && step.code_example && (
                                  <div className={`mt-2 p-3 rounded font-mono text-sm ${isDarkMode ? 'bg-zinc-950 text-emerald-400' : 'bg-slate-900 text-emerald-400'}`}>
                                    {step.code_example}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Validation Commands */}
                    {selectedPlaybook.validation_commands && (
                      <div>
                        <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          Validation Commands
                        </h4>
                        <div className="space-y-2">
                          {selectedPlaybook.validation_commands.map((cmd, idx) => (
                            <div key={idx} className={`p-3 rounded font-mono text-sm flex justify-between items-center ${isDarkMode ? 'bg-zinc-950 text-cyan-400' : 'bg-slate-900 text-cyan-400'}`}>
                              <span>{cmd}</span>
                              <button 
                                onClick={() => { navigator.clipboard.writeText(cmd); toast.success('Copied!'); }}
                                className="p-1 hover:bg-white/10 rounded"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* References */}
                    {selectedPlaybook.references && (
                      <div>
                        <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          References
                        </h4>
                        <ul className={`list-disc list-inside space-y-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                          {selectedPlaybook.references.map((ref, idx) => (
                            <li key={idx} className="text-sm">{ref}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {analytics && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                      <Target className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {analytics.metrics?.cyber_hygiene_score || 0}%
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Cyber Hygiene Score</p>
                    </div>
                  </div>
                  <Progress value={analytics.metrics?.cyber_hygiene_score || 0} className="h-2" />
                </div>
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {analytics.metrics?.mttr_days || 0}d
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>MTTR (Days)</p>
                    </div>
                  </div>
                </div>
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {analytics.metrics?.success_rate || 0}%
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Success Rate</p>
                    </div>
                  </div>
                </div>
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {analytics.ai_stats?.total_fixes_generated || 0}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>AI Fixes Generated</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                  <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Tasks by Status</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Pending', value: analytics.summary?.pending || 0, color: '#71717a' },
                            { name: 'In Progress', value: analytics.summary?.in_progress || 0, color: '#3b82f6' },
                            { name: 'Awaiting', value: analytics.summary?.awaiting_approval || 0, color: '#eab308' },
                            { name: 'Verified', value: analytics.summary?.verified || 0, color: '#22c55e' },
                            { name: 'Failed', value: analytics.summary?.failed || 0, color: '#ef4444' }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          dataKey="value"
                        >
                          {[
                            { color: '#71717a' },
                            { color: '#3b82f6' },
                            { color: '#eab308' },
                            { color: '#22c55e' },
                            { color: '#ef4444' }
                          ].map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#fff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className={`p-5 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                  <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Tasks by Team</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={Object.entries(analytics.by_team || {}).map(([name, value]) => ({ name, value }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'rgba(255,255,255,0.1)' : '#e2e8f0'} />
                        <XAxis dataKey="name" stroke={isDarkMode ? '#71717a' : '#64748b'} fontSize={12} />
                        <YAxis stroke={isDarkMode ? '#71717a' : '#64748b'} fontSize={12} />
                        <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#18181b' : '#fff', border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0' }} />
                        <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Ask AI Tab */}
        <TabsContent value="askai" className="space-y-4" data-testid="ask-ai-tab">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Chat Panel */}
            <div className={`lg:col-span-2 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Remediation AI Assistant</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Ask questions about fixes, vulnerabilities, and security best practices</p>
                  </div>
                </div>
              </div>
              
              {/* Response Area */}
              <div className="p-4 min-h-[300px] max-h-[500px] overflow-y-auto">
                {askAiResponse ? (
                  <div className="space-y-4">
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-100'}`}>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>You asked:</p>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{askAiResponse.question}</p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                      {askAiResponse.fallback && (
                        <div className={`mb-2 text-xs px-2 py-1 rounded inline-block ${isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                          Using cached knowledge
                        </div>
                      )}
                      <p className={`text-sm whitespace-pre-wrap ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{askAiResponse.response}</p>
                      <p className={`text-xs mt-3 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>{askAiResponse.timestamp}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-center">
                    <HelpCircle className={`w-12 h-12 mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                    <p className={`font-medium ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Ask me anything about remediation</p>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>I can help with vulnerability fixes, playbooks, and security best practices</p>
                  </div>
                )}
              </div>
              
              {/* Input Area */}
              <div className={`p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                <div className="flex gap-2">
                  <Input
                    value={askAiQuestion}
                    onChange={(e) => setAskAiQuestion(e.target.value)}
                    placeholder="Ask about patches, fixes, validation steps, playbooks..."
                    className={`flex-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
                    onKeyPress={(e) => e.key === 'Enter' && askAiAboutFix()}
                    disabled={askAiLoading}
                    data-testid="ask-ai-input"
                  />
                  <Button
                    onClick={askAiAboutFix}
                    disabled={askAiLoading || !askAiQuestion.trim()}
                    className="bg-violet-600 hover:bg-violet-500"
                    data-testid="ask-ai-submit"
                  >
                    {askAiLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Sidebar - Quick Questions & History */}
            <div className="space-y-4">
              {/* Quick Questions */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Quick Questions</h4>
                <div className="space-y-2">
                  {[
                    "How do I patch a critical CVE?",
                    "What's the best way to validate a fix?",
                    "How do approval workflows work?",
                    "Explain SQL injection remediation",
                    "How to prioritize vulnerabilities?"
                  ].map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setAskAiQuestion(q); }}
                      className={`w-full text-left text-sm p-2 rounded-lg transition-colors ${
                        isDarkMode 
                          ? 'text-zinc-400 hover:bg-zinc-800 hover:text-white' 
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <ArrowRightCircle className="w-3 h-3 inline mr-2" />
                      {q}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* History */}
              {askAiHistory.length > 0 && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
                  <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Recent Questions</h4>
                  <div className="space-y-2">
                    {askAiHistory.slice(0, 5).map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setAskAiResponse(item)}
                        className={`w-full text-left text-xs p-2 rounded-lg truncate transition-colors ${
                          isDarkMode 
                            ? 'text-zinc-400 hover:bg-zinc-800' 
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {item.question}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* AI Fix Dialog */}
      <Dialog open={fixDialogOpen} onOpenChange={setFixDialogOpen}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10' : ''}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
              <Brain className="w-5 h-5 text-violet-400" />
              AI Fix Generator Studio
            </DialogTitle>
          </DialogHeader>
          
          {aiFixLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-violet-500 mb-4" />
              <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>Analyzing vulnerability and generating remediation plan...</p>
              <p className={`text-sm mt-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`}>This may take 15-30 seconds</p>
            </div>
          ) : currentAiFix?.ai_response ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Executive Summary</h4>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  {currentAiFix.ai_response.summary || currentAiFix.ai_response.raw_response?.substring(0, 500)}
                </p>
              </div>

              {/* Root Cause */}
              {currentAiFix.ai_response.root_cause && (
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-100'}`}>
                  <h4 className={`font-semibold mb-2 text-red-500`}>Root Cause</h4>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{currentAiFix.ai_response.root_cause}</p>
                </div>
              )}

              {/* Remediation Options */}
              {currentAiFix.ai_response.remediation_options && (
                <div>
                  <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Remediation Options</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {currentAiFix.ai_response.remediation_options.map((option, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${option.recommended ? 'border-green-500/50' : ''} ${isDarkMode ? 'bg-zinc-800/30 border-white/10' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {option.recommended && <CheckCircle className="w-4 h-4 text-green-500" />}
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{option.name}</span>
                        </div>
                        <p className={`text-sm mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{option.description}</p>
                        <div className="flex gap-2 text-xs">
                          <span className={`px-2 py-0.5 rounded ${option.effectiveness === 'high' ? 'bg-green-500/20 text-green-400' : option.effectiveness === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                            {option.effectiveness} effectiveness
                          </span>
                          <span className={`px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-slate-100 text-slate-600'}`}>
                            {option.implementation_effort} effort
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Code Fix */}
              {currentAiFix.ai_response.primary_fix && (
                <div>
                  <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Primary Fix</h4>
                  
                  {/* Steps */}
                  {currentAiFix.ai_response.primary_fix.steps && (
                    <div className="mb-4">
                      <p className={`text-sm mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Implementation Steps:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        {currentAiFix.ai_response.primary_fix.steps.map((step, idx) => (
                          <li key={idx} className={`text-sm ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Before/After Code */}
                  {(currentAiFix.ai_response.primary_fix.code_before || currentAiFix.ai_response.primary_fix.code_after) && (
                    <div className="grid grid-cols-2 gap-4">
                      {currentAiFix.ai_response.primary_fix.code_before && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Before</span>
                            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(currentAiFix.ai_response.primary_fix.code_before)}>
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <pre className={`p-3 rounded-lg text-xs overflow-x-auto ${isDarkMode ? 'bg-red-500/10 text-zinc-300' : 'bg-red-50 text-slate-700'}`}>
                            {currentAiFix.ai_response.primary_fix.code_before}
                          </pre>
                        </div>
                      )}
                      {currentAiFix.ai_response.primary_fix.code_after && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>After</span>
                            <Button size="sm" variant="ghost" onClick={() => copyToClipboard(currentAiFix.ai_response.primary_fix.code_after)}>
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <pre className={`p-3 rounded-lg text-xs overflow-x-auto ${isDarkMode ? 'bg-green-500/10 text-zinc-300' : 'bg-green-50 text-slate-700'}`}>
                            {currentAiFix.ai_response.primary_fix.code_after}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Script */}
                  {currentAiFix.ai_response.primary_fix.script && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>Remediation Script</span>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(currentAiFix.ai_response.primary_fix.script)}>
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <pre className={`p-3 rounded-lg text-xs overflow-x-auto ${isDarkMode ? 'bg-violet-500/10 text-zinc-300' : 'bg-violet-50 text-slate-700'}`}>
                        {currentAiFix.ai_response.primary_fix.script}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Validation */}
              {currentAiFix.ai_response.validation && (
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'}`}>
                  <h4 className={`font-semibold mb-2 text-blue-500`}>Validation Steps</h4>
                  <ol className="list-decimal list-inside space-y-1">
                    {currentAiFix.ai_response.validation.steps?.map((step, idx) => (
                      <li key={idx} className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Feedback */}
              <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-zinc-800/30 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                <p className={`text-sm mb-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Was this AI-generated fix helpful?</p>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setFeedbackRating(5); setFeedbackDialogOpen(true); }}
                    className={isDarkMode ? 'border-green-500/30 text-green-400' : ''}
                  >
                    <ThumbsUp className="w-4 h-4 mr-1" />
                    Helpful
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setFeedbackRating(1); setFeedbackDialogOpen(true); }}
                    className={isDarkMode ? 'border-red-500/30 text-red-400' : ''}
                  >
                    <ThumbsDown className="w-4 h-4 mr-1" />
                    Not Helpful
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className={isDarkMode ? 'text-zinc-400' : 'text-slate-500'}>No fix data available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={createTaskDialogOpen} onOpenChange={setCreateTaskDialogOpen}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Create Remediation Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Vulnerability</Label>
              <p className={`mt-1 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                {selectedIssue?.title}
              </p>
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Remediation Type</Label>
              <Select value={newTask.remediation_type} onValueChange={(v) => setNewTask({...newTask, remediation_type: v})}>
                <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="auto_patch">Auto Patch</SelectItem>
                  <SelectItem value="virtual_patch">Virtual Patch (WAF)</SelectItem>
                  <SelectItem value="config_hardening">Config Hardening</SelectItem>
                  <SelectItem value="code_fix">Code Fix</SelectItem>
                  <SelectItem value="isolation">Isolation</SelectItem>
                  <SelectItem value="temporary_control">Temporary Control</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Assign to Team</Label>
              <Select value={newTask.assigned_team} onValueChange={handleTeamChangeInCreateTask}>
                <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="dev">Development</SelectItem>
                  <SelectItem value="secops">SecOps</SelectItem>
                  <SelectItem value="it">IT Operations</SelectItem>
                  <SelectItem value="security">Security Team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Assign to User (Optional)</Label>
              <Select value={newTask.assigned_to || 'none'} onValueChange={(v) => setNewTask({...newTask, assigned_to: v === 'none' ? '' : v})}>
                <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}>
                  <SelectValue placeholder="Select a user..." />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="none">No user assigned</SelectItem>
                  {createTaskUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white ${user.avatar_color || 'bg-violet-500'}`}>
                          {user.initials}
                        </div>
                        <span>{user.name || user.email}</span>
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>({user.role})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {createTaskUsers.length === 0 && newTask.assigned_team && (
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                  Click to load users for {newTask.assigned_team} team
                </p>
              )}
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Priority</Label>
              <Select value={newTask.priority} onValueChange={(v) => setNewTask({...newTask, priority: v})}>
                <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Notes</Label>
              <Textarea
                value={newTask.notes}
                onChange={(e) => setNewTask({...newTask, notes: e.target.value})}
                className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createRemediationTask} className="bg-indigo-600 hover:bg-indigo-500">
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Review Remediation Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Task</Label>
              <p className={`mt-1 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                {selectedTaskForApproval?.vulnerability?.title}
              </p>
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Comments</Label>
              <Textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                placeholder="Add approval comments..."
                className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => approveTask(false)} variant="outline" className="border-red-500/30 text-red-400">
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button onClick={() => approveTask(true)} className="bg-green-600 hover:bg-green-500">
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : ''}>Provide Feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setFeedbackRating(star)}
                    className={`w-8 h-8 rounded ${star <= feedbackRating ? 'bg-yellow-500 text-white' : isDarkMode ? 'bg-zinc-800 text-zinc-500' : 'bg-slate-200 text-slate-400'}`}
                  >
                    {star}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Comments</Label>
              <Textarea
                value={feedbackComments}
                onChange={(e) => setFeedbackComments(e.target.value)}
                placeholder="Help us improve..."
                className={`mt-1 ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : ''}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitFeedback} className="bg-indigo-600 hover:bg-indigo-500">
              Submit Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Task Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>
              Are you sure you want to delete this task? This action cannot be undone.
            </p>
            {taskToDelete && (
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {taskToDelete.vulnerability?.title || 'Remediation Task'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Status: {taskToDelete.status?.replace('_', ' ')} • Priority: {taskToDelete.priority}
                </p>
              </div>
            )}
            {taskToDelete?.status === 'verified' && (
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-100'}`}>
                <p className="text-sm text-red-500">
                  Completed tasks cannot be deleted for audit purposes.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirmOpen(false)}
              className={isDarkMode ? 'border-white/10' : ''}
            >
              Cancel
            </Button>
            <Button 
              onClick={deleteTask}
              disabled={deleteLoading || taskToDelete?.status === 'verified'}
              className="bg-red-600 hover:bg-red-500"
              data-testid="confirm-delete-task"
            >
              {deleteLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Assignment Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
              <Users className="w-5 h-5 text-indigo-500" />
              Assign User to Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {taskToAssign && (
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {taskToAssign.vulnerability?.title || 'Remediation Task'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
                  Team: {taskToAssign.assigned_team} • Priority: {taskToAssign.priority}
                </p>
              </div>
            )}
            <div>
              <Label className={isDarkMode ? 'text-zinc-300' : ''}>Select User</Label>
              <p className={`text-xs mb-2 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                Only users with {taskToAssign?.assigned_team} team roles are shown
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {assignableUsers.length > 0 ? assignableUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUserId(user.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      selectedUserId === user.id
                        ? 'border-indigo-500 bg-indigo-500/10'
                        : isDarkMode 
                          ? 'border-white/10 hover:border-white/20 hover:bg-zinc-800/50' 
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`assign-user-option-${user.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium text-white ${user.avatar_color || 'bg-violet-500'}`}>
                      {user.initials}
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {user.name || user.email}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                        {user.role} • {user.email}
                      </p>
                    </div>
                    {selectedUserId === user.id && (
                      <CheckCircle className="w-5 h-5 text-indigo-500" />
                    )}
                  </button>
                )) : (
                  <p className={`text-center py-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                    No assignable users found for this team
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setAssignDialogOpen(false)}
              className={isDarkMode ? 'border-white/10' : ''}
            >
              Cancel
            </Button>
            <Button 
              onClick={assignUserToTask}
              disabled={!selectedUserId}
              className="bg-indigo-600 hover:bg-indigo-500"
              data-testid="confirm-assign-user"
            >
              <Users className="w-4 h-4 mr-2" />
              Assign User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
